import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  AddressBook, Plus, Trash, PencilSimple, Star, Phone, MapPin, MagnifyingGlass, X
} from '@phosphor-icons/react';

const SERVICE_TYPES = ['Plumber', 'Electrician', 'Carpenter', 'Pest Control', 'AC Repair', 'Painter', 'Appliance Repair', 'Other'];

const TYPE_COLORS = {
  'Plumber': '#3B82F6',
  'Electrician': '#F59E0B',
  'Carpenter': '#A855F7',
  'Pest Control': '#EF4444',
  'AC Repair': '#00E5FF',
  'Painter': '#22C55E',
  'Appliance Repair': '#FF6B35',
  'Other': '#737373',
};

export default function ProvidersPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [form, setForm] = useState({ name: '', service_type: 'Plumber', phone: '', alt_phone: '', address: '', rating: 0, notes: '' });

  const fetchProviders = async () => {
    try {
      const params = {};
      if (typeFilter) params.service_type = typeFilter;
      const res = await api.get('/providers', { params });
      setProviders(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchProviders(); }, [api, typeFilter]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/providers/${editing}`, form);
      } else {
        await api.post('/providers', form);
      }
      closeForm();
      fetchProviders();
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/providers/${id}`); fetchProviders(); } catch (e) { console.error(e); }
  };

  const handleRate = async (id, rating) => {
    try { await api.post(`/providers/${id}/rate`, { rating }); fetchProviders(); } catch (e) { console.error(e); }
  };

  const openEdit = (p) => {
    setForm({ name: p.name, service_type: p.service_type, phone: p.phone, alt_phone: p.alt_phone || '', address: p.address || '', rating: p.rating || 0, notes: p.notes || '' });
    setEditing(p.provider_id);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm({ name: '', service_type: 'Plumber', phone: '', alt_phone: '', address: '', rating: 0, notes: '' });
  };

  const filtered = searchQ
    ? providers.filter(p => p.name.toLowerCase().includes(searchQ.toLowerCase()) || p.service_type.toLowerCase().includes(searchQ.toLowerCase()))
    : providers;

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="providers-page" className="space-y-6 relative">
      <div className="glow-orb-teal -top-20 right-0 z-0" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 relative z-10">
        <div>
          <p className="overline">Directory</p>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">{t('service_providers')}</h1>
          <p className="text-sm text-neutral-500 mt-1">Your trusted service provider contacts</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="add-provider-btn"
          className="px-5 py-2.5 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow flex items-center gap-2 w-fit">
          <Plus size={16} /> {t('add_provider')}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 relative z-10">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)}
            placeholder="Search providers..." data-testid="provider-search-input"
            className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setTypeFilter('')}
            className={`px-3 py-2 rounded-full text-xs font-medium transition-colors ${!typeFilter ? 'bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/30' : 'bg-white/5 text-neutral-400 border border-white/10 hover:bg-white/10'}`}>
            All
          </button>
          {SERVICE_TYPES.map(st => (
            <button key={st} onClick={() => setTypeFilter(st)} data-testid={`provider-filter-${st.toLowerCase().replace(' ', '-')}`}
              className={`px-3 py-2 rounded-full text-xs font-medium transition-colors ${typeFilter === st ? 'bg-[#FF6B35]/15 text-[#FF6B35] border border-[#FF6B35]/30' : 'bg-white/5 text-neutral-400 border border-white/10 hover:bg-white/10'}`}>
              {st}
            </button>
          ))}
        </div>
      </div>

      {/* Providers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
        {filtered.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <AddressBook size={48} weight="duotone" className="mx-auto text-neutral-600 mb-4" />
            <p className="text-neutral-500 text-sm">No providers found</p>
          </div>
        ) : filtered.map(p => {
          const color = TYPE_COLORS[p.service_type] || '#737373';
          return (
            <div key={p.provider_id} className="glass-card p-6 animate-fade-up" data-testid={`provider-card-${p.provider_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: `${color}15` }}>
                    <span className="text-base font-bold" style={{ color }}>{p.name[0]}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{p.name}</p>
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: `${color}15`, color }}>
                      {p.service_type}
                    </span>
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-0.5 mb-3" data-testid={`provider-rating-${p.provider_id}`}>
                {[1, 2, 3, 4, 5].map(star => (
                  <button key={star} onClick={() => handleRate(p.provider_id, star)} className="transition-transform hover:scale-125">
                    <Star size={16} weight={star <= (p.rating || 0) ? 'fill' : 'regular'}
                      className={star <= (p.rating || 0) ? 'text-amber-400' : 'text-neutral-600'} />
                  </button>
                ))}
                {p.last_used && <span className="text-[10px] text-neutral-500 ml-2">Last used: {p.last_used}</span>}
              </div>

              {/* Contact Info */}
              <div className="space-y-1.5 mb-3">
                <div className="flex items-center gap-2 text-xs text-neutral-400">
                  <Phone size={13} className="text-neutral-500" />
                  <a href={`tel:${p.phone}`} className="hover:text-[#FF6B35] transition-colors">{p.phone}</a>
                  {p.alt_phone && <span className="text-neutral-600">|</span>}
                  {p.alt_phone && <a href={`tel:${p.alt_phone}`} className="hover:text-[#FF6B35] transition-colors">{p.alt_phone}</a>}
                </div>
                {p.address && (
                  <div className="flex items-start gap-2 text-xs text-neutral-500">
                    <MapPin size={13} className="mt-0.5 flex-shrink-0" />
                    <span>{p.address}</span>
                  </div>
                )}
              </div>

              {p.notes && <p className="text-xs text-neutral-500 mb-3 line-clamp-2">{p.notes}</p>}

              <div className="flex gap-2">
                <button onClick={() => openEdit(p)} data-testid={`edit-provider-${p.provider_id}`}
                  className="flex-1 py-2 rounded-full border border-white/10 text-xs font-medium text-neutral-300 hover:bg-white/5 flex items-center justify-center gap-1">
                  <PencilSimple size={14} /> {t('edit')}
                </button>
                <button onClick={() => handleDelete(p.provider_id)} data-testid={`delete-provider-${p.provider_id}`}
                  className="py-2 px-3 rounded-full border border-white/10 text-xs text-neutral-500 hover:text-red-400 hover:border-red-400/20 transition-colors">
                  <Trash size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeForm}>
          <div className="bg-[#141414]/95 backdrop-blur-3xl border border-white/10 rounded-[24px] p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading font-semibold text-white text-lg mb-5">{editing ? 'Edit Provider' : t('add_provider')}</h3>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="overline block mb-2">{t('name')}</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                  data-testid="provider-name-input" placeholder="e.g., Ramesh Kumar"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="overline block mb-2">{t('service_type')}</label>
                <select value={form.service_type} onChange={e => setForm({ ...form, service_type: e.target.value })}
                  data-testid="provider-type-select"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50">
                  {SERVICE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-2">{t('phone')}</label>
                  <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required
                    data-testid="provider-phone-input" placeholder="+91 98765 43210"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
                <div>
                  <label className="overline block mb-2">Alt Phone</label>
                  <input type="tel" value={form.alt_phone} onChange={e => setForm({ ...form, alt_phone: e.target.value })}
                    data-testid="provider-altphone-input" placeholder="Optional"
                    className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
                </div>
              </div>
              <div>
                <label className="overline block mb-2">{t('address')}</label>
                <input type="text" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                  data-testid="provider-address-input" placeholder="Area / Locality"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50" />
              </div>
              <div>
                <label className="overline block mb-2">{t('notes')}</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                  data-testid="provider-notes-input" placeholder="e.g., Available on weekends"
                  className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-[#FF6B35]/50 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeForm} className="flex-1 py-3 rounded-full border border-white/10 text-neutral-400 text-sm font-medium hover:bg-white/5">{t('cancel')}</button>
                <button type="submit" data-testid="save-provider-btn" className="flex-1 py-3 rounded-full bg-[#FF6B35] text-white text-sm font-semibold btn-glow">{t('save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
